import { Component, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { collection, CollectionState } from '@/services/collection';

@Component
export class ProductFilters extends ThemeComponent {
    @Ref() group!: HTMLElement[];
    @Ref() option!: HTMLInputElement[];
    @Ref() closeFilters!: HTMLButtonElement[];
    @Ref() mobileSubmit!: HTMLElement;
    @Ref() clearAll!: HTMLElement;

    mobileBreakpoint: number = 768;

    init() {

        // Handle group open
        this.handleGroupOpen();

        // Handle option change
        this.handleOptionChange();

        // Handle close filter menu
        this.handleCloseFilterMenu();

        // Handle mobile submit
        this.handleMobileSubmit();

        // Handle clear all
        this.handleClearAll();

        // Listen for outer filters click
        this.listenForOuterFiltersClick();
    }

    private handleGroupOpen() {
        for (const group of this.group) {
            this.$listen(group, 'click', (event) => {

                // Do not continue for child clicks
                const clickedElement = event.target as HTMLElement;
                const groupOptions = group.querySelector('.product-filters__group-options');
                if (groupOptions && (clickedElement == groupOptions || groupOptions.contains(clickedElement))) {
                    return;
                }

                // Remove open class from other groups
                for (const sibling of this.group) {
                    if (group !== sibling) {
                        sibling.classList.remove('product-filters__group--open');
                    }
                }

                // Add open class to this group
                group.classList.toggle('product-filters__group--open');
            });
        }
    }

    private handleOptionChange() {
        for (const option of this.option) {
            this.$listen(option, 'change', () => {

                // Check device width
                if (window.innerWidth > this.mobileBreakpoint) {

                    // Prepare new collection URL
                    this.prepareNewCollectionUrl();
                }

                // Update active filter count
                this.updateActiveFilterCount();
            });
        }
    }

    private updateActiveFilterCount() {
        for (const group of this.group) {

            // Get selected options length
            const selectedOptions = group.querySelectorAll('[data-ref="option"]:checked');
            const selectedOptionsLength = selectedOptions.length;

            // Update text content & visibility of groups active filter count
            const groupActiveFilterCount = group.querySelector('.product-filters__active-filter-count');
            if (groupActiveFilterCount) {
                groupActiveFilterCount.textContent = selectedOptionsLength.toString();
                if (selectedOptionsLength === 0) {
                    groupActiveFilterCount.classList.add('product-filters__active-filter-count--hidden');
                } else {
                    groupActiveFilterCount.classList.remove('product-filters__active-filter-count--hidden');
                }
            }
        }
    }

    private handleCloseFilterMenu() {
        for (const closeFilter of this.closeFilters) {
            this.$listen(closeFilter, 'click', () => {

                // Only run if filters are active
                if (!document.body.classList.contains('product-filters-active')) {
                    return;
                }

                // Body class
                document.body.classList.remove('product-filters-active');

                // Filters class
                this.classList.remove('product-filters--active');
            });
        }
    }

    private handleMobileSubmit() {
        this.$listen(this.mobileSubmit, 'click', (e) => {

            // Prevent default
            e.preventDefault();

            // Prepare new collection URL
            this.prepareNewCollectionUrl();

            // Remove active class
            document.body.classList.remove('product-filters-active');
            this.classList.remove('product-filters--active');
        });
    }

    private prepareNewCollectionUrl() {

        // Get filter params
        const checkedInputs = document.querySelectorAll('[data-ref="option"]:checked') as NodeListOf<HTMLInputElement>;
        const checkedInputsArray = Array.from(checkedInputs);
        const params = checkedInputsArray.reduce((p, i) => {
            const values = p[i.name] || [];
            values.push(i.value);

            return {
                ...p,
                [i.name]: values,
            };
        }, {} as Record<string, string[]>);

        // Define new state
        const state = collection.state;
        const newState: CollectionState = {
            ...state,
            page: undefined,
            filters: params,
        };

        // Load new section
        void collection.update(newState);
    }

    private handleClearAll() {
        this.$listen(this.clearAll, 'click', (e) => {

            // Prevent default
            e.preventDefault();

            // Define new state
            const state = collection.state;
            const newState: CollectionState = {
                ...state,
                filters: {},
                page: undefined,
            };

            // Load new section
            void collection.update(newState);

            // Remove active class
            document.body.classList.remove('product-filters-active');
            this.classList.remove('product-filters--active');

            // Handle filter remove
            this.handleFilterRemoved('all');
        });
    }

    public handleFilterRemoved(filterId: string) {

        // Uncheck option based on filter removed
        if (filterId == 'all') {
            const filtersToUncheck = document.querySelectorAll('[data-ref="option"]:checked');
            for (const filterToUncheck of filtersToUncheck) {
                const input = filterToUncheck as HTMLInputElement;
                input.checked = false;
            }
        } else if (filterId == 'price') {
            // TODO
        } else {
            const filterToUncheck = document.getElementById(`${filterId}`) as HTMLInputElement;
            if (filterToUncheck) {
                filterToUncheck.checked = false;
            }
        }

        // Update active filter count
        this.updateActiveFilterCount();
    }

    private listenForOuterFiltersClick() {
        this.$listen(document.body, 'click', (e) => {

            // Ensure we have access to the clicked element
            let clickedElement = null;
            if (e.target) {
                clickedElement = e.target as HTMLElement;
            } else {
                return;
            }

            // Prevent default if filters are active (mobile)
            // eslint-disable-next-line max-len
            if (document.body.classList.contains('product-filters-active') && clickedElement !== this && !this.contains(clickedElement)) {
                e.preventDefault();
            }

            // Check device width
            if (window.innerWidth > this.mobileBreakpoint) {

                // Determine if clicked element is within a group
                let innerClick = false;
                for (const group of this.group) {
                    if (clickedElement === group || group.contains(clickedElement)) {
                        innerClick = true;
                    }
                }

                // Remove open class (if neccessary)
                if (!innerClick) {
                    for (const group of this.group) {
                        group.classList.remove('product-filters__group--open');
                    }
                }
            } else {

                // Determine if clicked element is within the filters
                // eslint-disable-next-line max-len
                if (clickedElement !== this && !this.contains(clickedElement)) {

                    // Determine if clicked element is the toggle button
                    const filterToggleButton = document.querySelector('.filter-toggle__button');
                    // eslint-disable-next-line max-len
                    if (filterToggleButton !== null && clickedElement !== filterToggleButton && !filterToggleButton.contains(clickedElement)) {

                        // Remove active class
                        document.body.classList.remove('product-filters-active');
                        this.classList.remove('product-filters--active');
                    }
                }
            }
        });
    }
}
